import { Dispatch, SetStateAction } from 'react';

import {
  ICartEntry,
  IComboSlot,
  IItem,
  ISection,
  MenuObject,
  PickerSelection,
} from '@rbi-ctg/menu';
import { ComboSlotType } from 'components/product-detail-v2/product-customization/types';
import { MenuObjectTypes } from 'enums/menu';
import { IModifierSelectionAction } from 'utils/wizard';

export enum ProductWizardStep {
  ProductHome,
  ProductCustomization,
  ProductReview,
  ProductEditOrder,
}

export type ProductMenuObject = Exclude<MenuObject, ISection>;

export type UserSelectionPickerAspect = { [id: string]: string };

export interface IUserSelectionComboSlotData {
  comboSlot: IComboSlot | null;
  selectedItem: IItem | null;
}
export type UserSelectionComboSlot = Map<string, IUserSelectionComboSlotData>;

export type IModifierSelectionActionWithData = IModifierSelectionAction & {
  isSanityDefaultSelection: boolean;
};

export type UserSelectionModifierMap = {
  [modifierOptionKey: string]: IModifierSelectionActionWithData;
};

export type UserSelectionModifierSlotMap = {
  [modifierKey: string]: UserSelectionModifierMap;
};

export type UserSelectionModifier = {
  [slotKey: string]: UserSelectionModifierSlotMap;
};

export interface IUserSelection {
  pickerAspects?: UserSelectionPickerAspect;
  comboSlot?: UserSelectionComboSlot;
  modifiers: UserSelectionModifier;
}

/**
 * Tracks if the user made any modifications on the selection options.
 */
export interface IUserManualModifications {
  pickerAspects: boolean;
  comboSlot: boolean;
  modifiers: boolean;
}

export type Product = PickerSelection;

export type ProductOption = IItem | IComboSlot;

export type ProductToCustomize = { product: IItem; slotKey: string };

export type SubmitCustomizationsFn = (args: {
  modifiers: UserSelectionModifierSlotMap;
  selectedItem: IItem;
  slotKey: string;
}) => void;

export interface IProductWizardFlowStep {
  id: string;
  type: MenuObjectTypes;
  currentStep: boolean;
  comboSlotType?: ComboSlotType;
  isFirstStep?: boolean;
  isPreviousStep?: boolean;
}

export interface IProductWizardFlowSelectionsBase {
  id: string;
  /**
   * Tracks if there are unsaved changes on the current PDP flow step.
   */
  isDirty: boolean;
  /**
   * Tracks if the PDP flow step has been saved.
   */
  isSaved?: boolean;
}

export interface IProductWizardFlowSelections extends IProductWizardFlowSelectionsBase {
  type: MenuObjectTypes;
}

export interface IProductWizardContext {
  /**
   * The raw menu data object passed to wizard
   */
  menuObject: ProductMenuObject;
  /**
   * The order quantity for the selected product
   */
  productQuantity: number;
  /**
   * The selected Combo/Item that can be customized and added to cart
   */
  selectedProduct: Product;
  /**
   * Sets the order quantity for the selected product
   */
  setProductQuantity: React.Dispatch<React.SetStateAction<number>>;
  /**
   * Method to submit customizations
   */
  submitCustomizations: SubmitCustomizationsFn;
  /**
   * The user's current selections (pickerAspects, comboSlots, modifiers)
   */
  userSelections: IUserSelection;
  /**
   * Tracks if the user has made any modifications to each selection (pickerAspects, comboSlots, modifiers)
   */
  manualModifications: IUserManualModifications;
  /**
   * Method to update picker aspect selection
   */
  updatePickerAspectSelections: (args: UserSelectionPickerAspect) => void;
  /**
   * Indicates if there have been any user modifications to any combo item
   */
  isDirty: boolean;
  singleOrMultipleModifierSelection: SingleOrMultipleModifierSelection | null;
  setSingleOrMultipleModifierSelection: Dispatch<
    SetStateAction<SingleOrMultipleModifierSelection | null>
  >;
  updateSingleOrMultipleModifierSelection: (
    slotKey: string,
    updateProps: Partial<SingleOrMultipleModifierSelection[string]>
  ) => void;
  productWizardFlowSteps: IProductWizardFlowStep[];
  updateProductWizardFlowSteps: (params: IProductWizardFlowStep) => void;
  /**
   * A dictionary that keeps track of whether each picker aspect has been updated.
   *
   * The key is the ID of the picker aspect, and the value is an object with two properties:
   * - id: The ID of the picker aspect.
   * - updated: A boolean indicating whether the picker aspect has been updated.
   */
  pickerAspectsUpdate: IProductWizardFlowSelectionsBase[];
  /**
   * A dictionary that keeps track of whether each combo slot has been updated on the pdp flow.
   *
   * The key is the ID of the combo slot, and the value is an object with two properties:
   * - id: The ID of the combo slot.
   * - updated: A boolean indicating whether the combo slot has been updated.
   */
  comboSlotUpdate: IProductWizardFlowSelectionsBase[];
  /**
   * A dictionary that keeps track of whether each item slot has been updated on the pdp flow.
   *
   * The key is the ID of the item slot, and the value is an object with two properties:
   * - id: The ID of the item slot.
   * - updated: A boolean indicating whether the item slot has been updated.
   */
  itemModifiersUpdate: IProductWizardFlowSelectionsBase[];
  /**
   * Used to track user selections and steps for the product wizard flow.
   */
  productWizardFlowUserSelections: IProductWizardFlowSelections[];
  /**
   * Used to track updated combo slot updates for pdp flow
   */
  updateSelectionStateWithId: ({ id, isDirty, type }: IProductWizardFlowSelections) => void;
  /**
   * Resets the following product wizard states:
   * - userSelections
   * - productWizardFlowSteps
   * - productWizardFlowUserSelections
   * - singleOrMultipleModifierSelection
   */
  resetProductWizard: () => void;
  /**
   * Tracks if we are navigating back in the PDP flow.
   */
  isBackNavigation: boolean;
  /**
   * Sets Back Navigation.
   */
  setIsBackNavigation: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Controls whether or not the close dialog should display.
   */
  showCloseDialog: boolean;
  /**
   * Displays the Close Dialog
   */
  handleCloseDialog: React.Dispatch<SetStateAction<boolean>>;
  /**
   * Controls whether or not the picker flow is dirty.
   */
  pickerFlowHasUpdates: boolean;
  /**
   * Resets item and combo selections updates for a picker flow
   */
  resetPickerFlowUpdates: () => void;
}

export interface IProductWizardProvider {
  product: ProductMenuObject;
}

export interface SetDefaultSelectionsArgs {
  menuObject: ProductMenuObject;
  pickerSelectionOverrides?: Record<string, string>;
  cartEntryOverrides?: ICartEntry;
}

export enum ModifierTypes {
  NON_NUMERIC = 'NON_NUMERIC',
  NUMERIC = 'NUMERIC',
  CHECKBOX = 'CHECKBOX',
  SINGLE_CHOICE = 'SINGLE_CHOICE',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
}

export type ProductWizardParams = {
  id: string;
  isEdit?: string;
  cartIdEditing?: string;
  isFavorite?: string;
  isFromCart?: string;
  isProductReviewFromCart?: string;
  rewardBenefitId?: string;
  rewardId?: string;
  currentStep?: string;
  slotKeyToCustomize?: string;
};

export interface NavigateToStepProps {
  step: ProductWizardStep;
  slotKey?: string;
}

export interface CustomizeProductParams {
  slotKey: string;
  step: ProductWizardStep;
}

export interface IProductWizardFlow {
  id: string;
  isEdit: boolean;
  cartIdEditing: string;
  isFavorite: boolean;
  isFromCart: boolean;
  isProductReviewFromCart: boolean;
  rewardBenefitId: string | undefined;
  rewardId: string | undefined;
  /**
   * Current step in the flow
   */
  currentStep: ProductWizardStep;
  /**
   * The slot key to be customized from combo slot selections
   */
  slotKeyToCustomize: string;
  /**
   * Navigates to the wizard home page
   */
  goToHome: () => void;
  /**
   * Navigates to the product customization screen
   */
  goToCustomizeProduct: (params: CustomizeProductParams) => void;
  /**
   * Navigates to the product review screen
   */
  goToReviewProduct: () => void;
}

export interface SingleOrMultipleModifierSelection {
  [id: string]: {
    key: string;
    name: string;
    maxAmount: number;
    type: ModifierTypes;
    isPristine: boolean;
    isSaved: boolean;
  } | null;
}

export interface ISingleOrMultipleModifierWasSaved {
  [slot: string]: boolean;
}

export interface ISingleOrMultipleModifierIsPristine {
  [slot: string]: boolean;
}
